<div class="metrics-dialog">
  <!-- Loading State with Skeleton -->
  @if (isLoading()) {
    <div class="metrics-content">
      <!-- Summary Statistics Grid Skeleton -->
      <div class="summary-grid">
        <!-- Total Uses Card Skeleton -->
        <div class="metric-card metric-card-primary">
          <div class="card-value">
            <p-skeleton width="4rem" height="2rem" />
          </div>
          <div class="card-footer">
            <i class="pi pi-chart-bar"></i>
            <span class="card-label">Total Uses</span>
          </div>
        </div>

        <!-- Unique Customers Card Skeleton -->
        <div class="metric-card metric-card-info">
          <div class="card-value">
            <p-skeleton width="3rem" height="2rem" />
          </div>
          <div class="card-footer">
            <i class="pi pi-users"></i>
            <span class="card-label">Unique Customers</span>
          </div>
        </div>

        <!-- Successful Card Skeleton -->
        <div class="metric-card metric-card-success">
          <div class="card-value">
            <p-skeleton width="4rem" height="2rem" />
          </div>
          <div class="card-footer">
            <i class="pi pi-check-circle"></i>
            <span class="card-label">Successful</span>
          </div>
        </div>

        <!-- Failed Card Skeleton -->
        <div class="metric-card metric-card-danger">
          <div class="card-value">
            <p-skeleton width="3rem" height="2rem" />
          </div>
          <div class="card-footer">
            <i class="pi pi-times-circle"></i>
            <span class="card-label">Failed</span>
          </div>
        </div>

        <!-- Success Rate Card Skeleton -->
        <div class="metric-card metric-card-violet">
          <div class="card-value">
            <p-skeleton width="5rem" height="2rem" />
          </div>
          <div class="card-footer">
            <i class="pi pi-percentage"></i>
            <span class="card-label">Success Rate</span>
          </div>
        </div>
      </div>

      <!-- Date Range Filter Skeleton -->
      <div class="filter-section">
        <p-skeleton width="100%" height="2.5rem" borderRadius="8px" />
      </div>

      <!-- Usage History Table Skeleton -->
      <div class="logs-section">
        <div class="logs-header">
          <div class="logs-header-left">
            <i class="pi pi-history"></i>
            <span class="logs-title">Usage History</span>
            <span class="separator">•</span>
            <p-skeleton width="6rem" height="0.875rem" />
            <span class="separator">•</span>
            <p-skeleton width="2rem" height="1.125rem" borderRadius="8px" />
          </div>
          <div class="logs-header-right">
            <p-skeleton width="7rem" height="2rem" borderRadius="6px" />
          </div>
        </div>

        <div class="table-container">
          <div class="table-skeleton">
            <!-- Table Header -->
            <div class="skeleton-table-header">
              <div class="skeleton-header-cell" style="min-width: 160px">
                Date & Time
              </div>
              <div class="skeleton-header-cell" style="min-width: 180px">
                Customer
              </div>
              <div class="skeleton-header-cell" style="min-width: 120px">
                Order
              </div>
              <div class="skeleton-header-cell" style="min-width: 100px">
                Status
              </div>
              <div class="skeleton-header-cell" style="min-width: 120px">
                IP Address
              </div>
            </div>

            <!-- Table Body Skeleton Rows -->
            <div class="skeleton-table-body">
              @for (row of [1, 2, 3, 4, 5, 6, 7, 8]; track row) {
                <div class="skeleton-table-row">
                  <div class="skeleton-cell" style="min-width: 160px">
                    <p-skeleton width="9rem" height="0.875rem" />
                  </div>
                  <div class="skeleton-cell" style="min-width: 180px">
                    <p-skeleton width="8rem" height="0.875rem" />
                  </div>
                  <div class="skeleton-cell" style="min-width: 120px">
                    <p-skeleton width="4rem" height="0.875rem" />
                  </div>
                  <div class="skeleton-cell" style="min-width: 100px">
                    <p-skeleton
                      width="4rem"
                      height="1.25rem"
                      borderRadius="4px"
                    />
                  </div>
                  <div class="skeleton-cell" style="min-width: 120px">
                    <p-skeleton width="6rem" height="0.875rem" />
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Pagination Skeleton -->
          <div class="pagination-container">
            <div class="skeleton-pagination">
              <p-skeleton width="10rem" height="1.5rem" />
              <div style="display: flex; gap: 0.5rem">
                <p-skeleton width="2rem" height="2rem" borderRadius="4px" />
                <p-skeleton width="2rem" height="2rem" borderRadius="4px" />
                <p-skeleton width="2rem" height="2rem" borderRadius="4px" />
              </div>
              <p-skeleton width="4rem" height="2rem" borderRadius="4px" />
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Metrics Content -->
  @if (!isLoading() && metrics()) {
    <div class="metrics-content">
      <!-- Summary Statistics Grid -->
      <div class="summary-grid">
        <!-- Total Uses Card -->
        <div class="metric-card metric-card-primary">
          <div class="card-value">{{ metrics()!.summary.totalUses }}</div>
          <div class="card-footer">
            <i class="pi pi-chart-bar"></i>
            <span class="card-label">Total Uses</span>
          </div>
        </div>

        <!-- Unique Customers Card -->
        <div class="metric-card metric-card-info">
          <div class="card-value">
            {{ metrics()!.summary.uniqueCustomers }}
          </div>
          <div class="card-footer">
            <i class="pi pi-users"></i>
            <span class="card-label">Unique Customers</span>
          </div>
        </div>

        <!-- Successful Card -->
        <div class="metric-card metric-card-success">
          <div class="card-value">{{ metrics()!.summary.successCount }}</div>
          <div class="card-footer">
            <i class="pi pi-check-circle"></i>
            <span class="card-label">Successful</span>
          </div>
        </div>

        <!-- Failed Card -->
        <div class="metric-card metric-card-danger">
          <div class="card-value">{{ metrics()!.summary.failureCount }}</div>
          <div class="card-footer">
            <i class="pi pi-times-circle"></i>
            <span class="card-label">Failed</span>
          </div>
        </div>

        <!-- Success Rate Card -->
        <div class="metric-card metric-card-violet">
          <div class="card-value">{{ getSuccessRateDisplay() }}</div>
          <div class="card-footer">
            <i class="pi pi-percentage"></i>
            <span class="card-label">Success Rate</span>
          </div>
        </div>
      </div>

      <!-- Compact Date Range Filter -->
      <div class="filter-section">
        <p-datepicker
          [formControl]="dateRangeControl"
          selectionMode="range"
          [showIcon]="true"
          [iconDisplay]="'input'"
          placeholder="Filter by date range..."
          dateFormat="mm/dd/yy"
          [readonlyInput]="true"
          [showButtonBar]="true"
          [showClear]="true"
          appendTo="body"
          styleClass="date-picker-compact"
          (onSelect)="onDateRangeSelect()"
          (onClear)="onDateRangeClear()"
        />
      </div>

      <!-- Usage History Table with Fixed Height and Scroll -->
      <div class="logs-section">
        <div class="logs-header">
          <div class="logs-header-left">
            <i class="pi pi-history"></i>
            <span class="logs-title">Usage History</span>
            <span class="separator">•</span>
            <span class="action-type-text">{{ quickLinkName }}</span>
            <span class="separator">•</span>
            <span class="logs-count">{{
              metrics()!.pagination.totalItems
            }}</span>
          </div>
          <div class="logs-header-right">
            <p-button
              label="Export CSV"
              icon="pi pi-download"
              size="small"
              [text]="true"
              (onClick)="exportToCsv()"
              [disabled]="!metrics()?.usageLogs?.length"
            />
          </div>
        </div>

        @if (metrics()!.usageLogs.length > 0) {
          <div class="table-container">
            <p-table
              [value]="metrics()!.usageLogs"
              [scrollable]="true"
              scrollHeight="400px"
              styleClass="p-datatable-sm compact-table"
              responsiveLayout="scroll"
              scrollDirection="both"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th style="min-width: 160px">Date & Time</th>
                  <th style="min-width: 180px">Customer</th>
                  <th style="min-width: 120px">Order</th>
                  <th style="min-width: 100px">Status</th>
                  <th style="min-width: 120px">IP Address</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-log>
                <tr>
                  <td>{{ formatDate(log.usedUtc) }}</td>
                  <td>
                    <div class="customer-info">
                      @if (log.customerName) {
                        <div class="customer-name">{{ log.customerName }}</div>
                      }
                      @if (log.customerEmail) {
                        <div class="customer-email">
                          {{ log.customerEmail }}
                        </div>
                      }
                      @if (!log.customerName && !log.customerEmail) {
                        <span class="text-muted">ID: {{ log.customerId }}</span>
                      }
                    </div>
                  </td>
                  <td>{{ log.scheduledOrderId }}</td>
                  <td>
                    <p-tag
                      [value]="log.success ? 'Success' : 'Failed'"
                      [severity]="getSuccessSeverity(log.success)"
                      styleClass="compact-tag"
                    />
                    @if (!log.success && log.errorMessage) {
                      <div class="error-message">{{ log.errorMessage }}</div>
                    }
                  </td>
                  <td>{{ log.ipAddress || "N/A" }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="5" class="text-center">
                    <div class="empty-message">
                      <i class="pi pi-info-circle"></i>
                      <p>No usage logs found</p>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>

            <!-- Sticky Pagination -->
            <div class="pagination-container">
              <p-paginator
                [rows]="pageSize"
                [totalRecords]="metrics()!.pagination.totalItems"
                [first]="(currentPage - 1) * pageSize"
                [rowsPerPageOptions]="[10, 25, 50, 100]"
                (onPageChange)="onPageChange($event)"
              />
            </div>
          </div>
        } @else {
          <div class="empty-state-inline">
            <i class="pi pi-chart-line"></i>
            <p>No usage data available yet</p>
            <small>This quick link hasn't been used yet</small>
          </div>
        }
      </div>
    </div>
  }

  <!-- No Data State -->
  @if (!isLoading() && !metrics()) {
    <div class="empty-state">
      <i class="pi pi-exclamation-circle" style="font-size: 3rem"></i>
      <p>Failed to load metrics</p>
      <p-button label="Retry" icon="pi pi-refresh" (onClick)="loadMetrics()" />
    </div>
  }
</div>
